import { useState } from 'react'
import {
    useGetConfigTemplates,
    useCreateConfigTemplate,
    useUpdateConfigTemplate,
    useDeleteConfigTemplate,
} from '../../../../api/routes/config-templates'
import { Button } from '../../../../components/ui/button'
import { Badge } from '../../../../components/ui/badge'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../../../components/ui/dialog'
import {
    Plus,
    Pencil,
    Trash2,
    ChevronDown,
    ChevronRight,
} from 'lucide-react'
import { toast } from 'react-toastify'
import {
    ConfigTemplateDialog,
    ConfigTemplateFormData,
} from './config-template-dialog'

const dataTypeBadgeVariant = (dataType: string) => {
    switch (dataType) {
        case 'number':
            return 'default' as const
        case 'boolean':
            return 'secondary' as const
        case 'enum':
            return 'secondary' as const
        default:
            return 'outline' as const
    }
}

type ConfigTemplatesSectionProps = {
    modelId: string
    canConfigure: boolean
}

export const ConfigTemplatesSection = ({
    modelId,
    canConfigure,
}: ConfigTemplatesSectionProps) => {
    const { data: templates, isLoading } = useGetConfigTemplates(modelId)
    const createTemplate = useCreateConfigTemplate()
    const updateTemplate = useUpdateConfigTemplate()
    const deleteTemplate = useDeleteConfigTemplate()

    const [expanded, setExpanded] = useState(true)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingTemplate, setEditingTemplate] = useState<any>(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [deletingTemplate, setDeletingTemplate] = useState<any>(null)

    const handleSave = (data: ConfigTemplateFormData) => {
        const options: any = {}
        if (data.dataType === 'number') {
            if (data.options.min !== '') options.min = Number(data.options.min)
            if (data.options.max !== '') options.max = Number(data.options.max)
            if (data.options.step !== '') options.step = Number(data.options.step)
        }
        if (data.dataType === 'boolean') {
            if (data.options.trueLabel) options.trueLabel = data.options.trueLabel
            if (data.options.falseLabel) options.falseLabel = data.options.falseLabel
        }
        if (data.dataType === 'enum') {
            options.choices = data.options.choices
        }
        if (data.dataType === 'version') {
            if (data.options.formatHint) options.formatHint = data.options.formatHint
        }

        const payload: any = {
            name: data.name.trim(),
            label: data.label.trim(),
            description: data.description.trim() || null,
            dataType: data.dataType,
            groupName: data.groupName.trim() || null,
            sortOrder: data.sortOrder !== '' ? Number(data.sortOrder) : 0,
            required: data.required,
            defaultValue: data.defaultValue.trim() || null,
            modelId,
            options: Object.keys(options).length > 0 ? options : null,
        }

        const onError = (error: any) => {
            toast.error(
                error?.response?.data?.message ||
                    error.message ||
                    'Failed to save template'
            )
        }

        if (editingTemplate) {
            updateTemplate.mutate(
                { id: editingTemplate.id, data: payload },
                {
                    onSuccess: () => {
                        toast.success('Template updated')
                        setDialogOpen(false)
                        setEditingTemplate(null)
                    },
                    onError,
                }
            )
        } else {
            createTemplate.mutate(payload, {
                onSuccess: () => {
                    toast.success('Template created')
                    setDialogOpen(false)
                },
                onError,
            })
        }
    }

    const handleDelete = () => {
        if (!deletingTemplate) return
        deleteTemplate.mutate(deletingTemplate.id, {
            onSuccess: () => {
                toast.success('Template deleted')
                setDeleteDialogOpen(false)
                setDeletingTemplate(null)
            },
            onError: (error: any) => {
                toast.error(
                    error?.response?.data?.message ||
                        error.message ||
                        'Failed to delete template'
                )
            },
        })
    }

    if (isLoading) return null

    const templateList = templates || []

    return (
        <div className="mt-8">
            <div
                className="flex items-center gap-2 cursor-pointer mb-3"
                onClick={() => setExpanded(!expanded)}
            >
                {expanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <h2 className="text-lg font-semibold tracking-tight text-foreground">
                    Configuration Templates
                </h2>
                <span className="text-xs text-muted-foreground">
                    ({templateList.length})
                </span>
                {canConfigure && expanded && (
                    <Button
                        size="sm"
                        variant="outline"
                        className="ml-auto"
                        onClick={(e) => {
                            e.stopPropagation()
                            setEditingTemplate(null)
                            setDialogOpen(true)
                        }}
                    >
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add Template
                    </Button>
                )}
            </div>

            {expanded && (
                <>
                    {templateList.length === 0 ? (
                        <div className="rounded-lg border bg-card p-12 text-center">
                            <p className="text-muted-foreground">
                                No configuration templates defined for this model yet.
                            </p>
                            {canConfigure && (
                                <Button
                                    variant="outline"
                                    className="mt-4"
                                    onClick={() => {
                                        setEditingTemplate(null)
                                        setDialogOpen(true)
                                    }}
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add First Template
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {templateList.map((tmpl: any) => (
                                <div
                                    key={tmpl.id}
                                    className="rounded-lg border bg-card overflow-hidden"
                                >
                                    <div className="flex items-center gap-3 px-4 py-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-sm truncate">
                                                    {tmpl.label}
                                                </span>
                                                <span className="text-xs text-muted-foreground font-mono">
                                                    {tmpl.name}
                                                </span>
                                                <Badge
                                                    variant={dataTypeBadgeVariant(tmpl.dataType)}
                                                    className="text-[10px] px-1.5 py-0"
                                                >
                                                    {tmpl.dataType}
                                                </Badge>
                                                {tmpl.groupName && (
                                                    <span className="text-xs text-muted-foreground">
                                                        {tmpl.groupName}
                                                    </span>
                                                )}
                                                {tmpl.required && (
                                                    <Badge
                                                        variant="destructive"
                                                        className="text-[10px] px-1.5 py-0"
                                                    >
                                                        required
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                        {canConfigure && (
                                            <div
                                                className="flex items-center gap-1.5 shrink-0"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <button
                                                    onClick={() => {
                                                        setEditingTemplate(tmpl)
                                                        setDialogOpen(true)
                                                    }}
                                                    title="Edit template"
                                                    className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setDeletingTemplate(tmpl)
                                                        setDeleteDialogOpen(true)
                                                    }}
                                                    title="Delete template"
                                                    className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            <ConfigTemplateDialog
                open={dialogOpen}
                onOpenChange={(open) => {
                    setDialogOpen(open)
                    if (!open) setEditingTemplate(null)
                }}
                onSave={handleSave}
                initialData={editingTemplate}
                isPending={createTemplate.isPending || updateTemplate.isPending}
            />

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Config Template</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-foreground">
                            Are you sure you want to permanently delete{' '}
                            <span className="font-semibold">
                                {deletingTemplate?.label || deletingTemplate?.name}
                            </span>
                            ? This will also remove all device settings that use this template.
                        </p>
                    </div>
                    <DialogFooter className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleteTemplate.isPending}
                        >
                            {deleteTemplate.isPending
                                ? 'Deleting...'
                                : 'Delete Template'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
